// Icon-provider contract. The shared components are icon-library-agnostic: each
// app provides its own icon set at the root via `app.provide(ICONS_KEY, ...)`,
// and shared components read it with useIcons(). Both apps feed Phosphor (web-ui
// via its Iconify `ph:*` registry, the extension via @phosphor-icons/vue
// components) - no icon library is bundled into this package, so there's no
// duplicate icon dependency and no lock-in.
import type { Component, InjectionKey } from 'vue';
import { inject } from 'vue';

/** The icon slots the shared components render. Each value is a Vue icon
 *  component that accepts a numeric `size` prop (Phosphor does). */
export interface HqIcons {
  globe:        Component; // web source
  chat:         Component; // workspace message
  brain:        Component; // memory
  externalLink: Component; // opens-in-new-tab affordance
  caretRight:   Component; // disclosure chevron (Fold)
  close:        Component; // dialog dismiss (X)
  search:       Component; // command-palette filter glyph (magnifier)
  copy:         Component; // copy-to-clipboard affordance (CopyButton)
  check:        Component; // copied-confirmation tick (CopyButton)
}

export const ICONS_KEY: InjectionKey<HqIcons> = Symbol('hqIcons');

// If an app forgets to provide icons, components render nothing for the icon slot
// (a missing `:is` is a no-op) rather than crashing - graceful, not silent-wrong.
const EMPTY = {} as HqIcons;

export function useIcons(): HqIcons {
  return inject(ICONS_KEY, EMPTY);
}
